import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import DataTypePill from 'app/components/CommonStyled/DataTypePill/DataTypePill';

import { ResourceDetailContext } from '../ResourceDetailContext';
import { ResourceSubtitle } from '../styled';
import {
  ResourceDetailExtraInformationContainer,
  ResourceDetailExtraInformationDataLabel,
  ResourceDetailExtraInformationDataPills,
  ResourceDetailExtraInformationDataWrapper,
  ResourceDetailExtraInformationImpactMetrics,
  ResourceDetailExtraInformationImpactMetricsWrapper,
  ResourceDetailExtraInformationShowMoreButton,
  ResourceDetailExtraInformationWrapper,
} from './styled';

export default function ResourceDetailExtraInformation() {
  const [clientsShowing, setClientsShowing] = useState<number>(3);
  const [projectCategoriesShowing, setProjectCategoriesShowing] = useState<number>(3);
  const [skillsShowing, setSkillsShowing] = useState<number>(3);
  const [showMoreByTheNumbers, setShowMoreByTheNumbers] = useState<boolean>(false);
  const [shouldShowMoreButton, setShouldShowMoreButton] = useState<boolean>(false);
  const impactMetricsRef = useRef<HTMLSpanElement>(null);
  const { data } = useContext(ResourceDetailContext);

  const amountOfColumns = useMemo(() => {
    let tempColumns = 0;

    if (data?.impact_metrics) tempColumns++;
    if (
      data?.project_categories?.length > 0 ||
      data?.skills?.length > 0 ||
      data?.clients?.length > 0
    )
      tempColumns++;

    return tempColumns;
  }, [data?.impact_metrics, data?.project_categories, data?.skills, data?.clients]);

  useEffect(() => {
    if (impactMetricsRef.current && data?.impact_metrics) {
      // Temporarily expand to measure full height
      const element = impactMetricsRef.current;
      const originalMaxHeight = element.style.maxHeight;
      const originalOverflow = element.style.overflow;

      element.style.maxHeight = 'none';
      element.style.overflow = 'visible';

      const fullHeight = element.scrollHeight;

      // Restore original styles
      element.style.maxHeight = originalMaxHeight;
      element.style.overflow = originalOverflow;

      setShouldShowMoreButton(fullHeight > 180);
    }
  }, [data?.impact_metrics]);

  return (
    <ResourceDetailExtraInformationWrapper columns={amountOfColumns}>
      {data?.project_categories?.length > 0 ||
      data?.skills?.length > 0 ||
      data?.clients?.length > 0 ? (
        <ResourceDetailExtraInformationContainer>
          <ResourceSubtitle>THE SPECIFICS</ResourceSubtitle>
          <ResourceDetailExtraInformationDataWrapper>
            {data?.clients?.length > 0 ? (
              <>
                <ResourceDetailExtraInformationDataLabel>
                  Project for:
                </ResourceDetailExtraInformationDataLabel>
                <ResourceDetailExtraInformationDataPills>
                  {data?.clients?.slice(0, clientsShowing).map((client) => (
                    <DataTypePill
                      key={client.slug}
                      title={client.name}
                      slug={client.slug}
                      type="client"
                      clickable={false}
                    />
                  ))}
                </ResourceDetailExtraInformationDataPills>
              </>
            ) : null}
            {data?.project_categories?.length > 0 ? (
              <>
                <ResourceDetailExtraInformationDataLabel>
                  Services:
                </ResourceDetailExtraInformationDataLabel>
                <ResourceDetailExtraInformationDataPills>
                  {data?.project_categories?.slice(0, projectCategoriesShowing).map((category) => (
                    <DataTypePill
                      key={category.slug}
                      title={category.name}
                      slug={category.slug}
                      type="project_category"
                      clickable={false}
                    />
                  ))}
                  {data?.project_categories?.length > projectCategoriesShowing ? (
                    <DataTypePill
                      title={`${data.project_categories.length - projectCategoriesShowing} more...`}
                      type="more"
                      onClick={() => setProjectCategoriesShowing(projectCategoriesShowing + 3)}
                    />
                  ) : null}
                </ResourceDetailExtraInformationDataPills>
              </>
            ) : null}
            {data?.skills?.length > 0 ? (
              <>
                <ResourceDetailExtraInformationDataLabel>
                  Key Skills:
                </ResourceDetailExtraInformationDataLabel>
                <ResourceDetailExtraInformationDataPills>
                  {data?.skills?.slice(0, skillsShowing).map((skill) => (
                    <DataTypePill
                      key={skill.slug}
                      title={skill.name}
                      slug={skill.slug}
                      type="skill"
                      clickable={false}
                    />
                  ))}
                  {data?.skills?.length > skillsShowing ? (
                    <DataTypePill
                      title={`${data.skills.length - skillsShowing} more...`}
                      type="more"
                      onClick={() => setSkillsShowing(skillsShowing + 3)}
                    />
                  ) : null}
                </ResourceDetailExtraInformationDataPills>
              </>
            ) : null}
          </ResourceDetailExtraInformationDataWrapper>
        </ResourceDetailExtraInformationContainer>
      ) : null}
      {data?.impact_metrics ? (
        <ResourceDetailExtraInformationContainer>
          <ResourceSubtitle>BY THE NUMBERS</ResourceSubtitle>
          <ResourceDetailExtraInformationImpactMetricsWrapper>
            <ResourceDetailExtraInformationImpactMetrics
              ref={impactMetricsRef}
              $isExpanded={showMoreByTheNumbers}
            >
              {data?.impact_metrics}
            </ResourceDetailExtraInformationImpactMetrics>
            {shouldShowMoreButton && !showMoreByTheNumbers ? (
              <ResourceDetailExtraInformationShowMoreButton
                onClick={() => setShowMoreByTheNumbers(!showMoreByTheNumbers)}
              >
                More
              </ResourceDetailExtraInformationShowMoreButton>
            ) : null}
          </ResourceDetailExtraInformationImpactMetricsWrapper>
        </ResourceDetailExtraInformationContainer>
      ) : null}
    </ResourceDetailExtraInformationWrapper>
  );
}
